import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { functionsBaseUrl } from '@/config/functionsUrl';
import { doc, updateDoc, collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '@/firebase';
import {
  ArrowLeft, Edit2, Save, X, Plus, Trash2, Loader2,
  Stethoscope, FileText, Shield, Phone, Pill,
  Calendar, AlertTriangle, Footprints, UtensilsCrossed,
  Scissors, BookOpen,
} from 'lucide-react';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface RoutineData {
  walkFrequency?: string;
  walkDuration?: string;
  walkTimes?: string;
  feedingBrand?: string;
  feedingPortion?: string;
  feedingTimes?: string;
  groomingFrequency?: string;
  lastGroomed?: string;
  trainingNotes?: string;
}

export interface VetInfo {
  name?: string;
  phone?: string;
  address?: string;
  lastVisit?: string;
  distance?: string;
}

export interface Medication {
  id: string;
  name: string;
  dose: string;
  frequency: string;
  startDate?: string;
  notes?: string;
}

export interface InsuranceData {
  provider?: string;
  policyNumber?: string;
  phone?: string;
  coverageType?: string;
  monthlyPremium?: string;
  renewalDate?: string;
  notes?: string;
}

export interface ExtendedPet {
  id: string;
  name: string;
  species: string;
  breed?: string;
  birthdateOrAge?: string;
  householdId: string;
  weight?: string;
  microchipId?: string;
  color?: string;
  routine?: RoutineData;
  vet?: VetInfo;
  emergencyVet?: VetInfo;
  medications?: Medication[];
  insurance?: InsuranceData;
}

interface MedicalRecord {
  id: string;
  type: string;
  date: string;
  notes?: string;
  nextDueDate?: string;
  cost?: number;
}

interface PetNote {
  id: string;
  text: string;
  createdAt: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SPECIES_ICONS: Record<string, string> = {
  dog: '🐕', cat: '🐱', bird: '🐦', reptile: '🦎', other: '🐾',
  DOG: '🐕', CAT: '🐱', BIRD: '🐦', REPTILE: '🦎', OTHER: '🐾',
};

async function apiFetch(path: string, method: string, body?: object): Promise<any> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`${functionsBaseUrl}/apiPets${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error ?? 'Request failed');
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Field({
  label, value, editing, inputKey, draft, setDraft, type = 'text', placeholder, span2,
}: {
  label: string; value?: string; editing: boolean;
  inputKey: string; draft: Record<string, string>;
  setDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  type?: string; placeholder?: string; span2?: boolean;
}) {
  return (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      {editing ? (
        <input
          type={type}
          value={draft[inputKey] ?? value ?? ''}
          onChange={e => setDraft(d => ({ ...d, [inputKey]: e.target.value }))}
          placeholder={placeholder}
          className="w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 border border-transparent focus:border-brand/20"
        />
      ) : (
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 min-h-[1.5rem]">
          {value || <span className="text-slate-400 italic font-normal">Not set</span>}
        </p>
      )}
    </div>
  );
}

interface SectionCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void | Promise<void>;
  accent?: 'amber';
}

function SectionCard({ title, icon, children, editing, onEdit, onCancel, onSave, accent }: SectionCardProps) {
  return (
    <div className={`bg-white dark:bg-slate-900 rounded-2xl border p-6 space-y-4 ${
      accent === 'amber'
        ? 'border-amber-100 dark:border-amber-900/30'
        : 'border-slate-100 dark:border-slate-800'
    }`}>
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-2 ${accent === 'amber' ? 'text-amber-500' : 'text-slate-500 dark:text-slate-400'}`}>
          {icon}
          <h3 className="text-xs font-black uppercase tracking-widest">{title}</h3>
        </div>
        {editing ? (
          <div className="flex gap-2">
            <button onClick={onCancel} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
              <X className="w-4 h-4" />
            </button>
            <button onClick={onSave} className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand-hover transition-colors">
              <Save className="w-3 h-3" /> Save
            </button>
          </div>
        ) : (
          <button onClick={onEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-brand text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand/10 transition-colors">
            <Edit2 className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ── PetProfile ─────────────────────────────────────────────────────────────────

interface PetProfileProps {
  pet: ExtendedPet;
  householdId: string;
  onBack: () => void;
  onUpdate: (updated: ExtendedPet) => void;
}

type ProfileTab = 'profile' | 'routine' | 'medical' | 'notes' | 'insurance';

// TABS are created dynamically inside PetProfile to use t()
const PET_TAB_IDS = ['profile', 'routine', 'medical', 'notes', 'insurance'] as const;

export const PetProfile: React.FC<PetProfileProps> = ({ pet, householdId, onBack, onUpdate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [petData, setPetData] = useState<ExtendedPet>(pet);
  const [saving, setSaving] = useState(false);

  // Medical
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [medicalLoading, setMedicalLoading] = useState(false);

  // Notes
  const [notes, setNotes] = useState<PetNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Profile edit
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState<Record<string, string>>({});

  // Routine edit (all sections share one edit state)
  const [editingRoutine, setEditingRoutine] = useState(false);
  const [routineDraft, setRoutineDraft] = useState<Record<string, string>>({});

  // Vet edits
  const [editingVet, setEditingVet] = useState(false);
  const [vetDraft, setVetDraft] = useState<Record<string, string>>({});
  const [editingEVet, setEditingEVet] = useState(false);
  const [eVetDraft, setEVetDraft] = useState<Record<string, string>>({});

  // Medications
  const [showAddMed, setShowAddMed] = useState(false);
  const [newMed, setNewMed] = useState({ name: '', dose: '', frequency: '', startDate: '', notes: '' });
  const [savingMed, setSavingMed] = useState(false);

  // Insurance edit
  const [editingIns, setEditingIns] = useState(false);
  const [insDraft, setInsDraft] = useState<Record<string, string>>({});

  // ── Data loaders ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (activeTab !== 'medical') return;
    setMedicalLoading(true);
    apiFetch(`/${pet.id}/medical?householdId=${householdId}`, 'GET')
      .then(res => setMedicalRecords(res?.data ?? []))
      .catch(() => {})
      .finally(() => setMedicalLoading(false));
  }, [activeTab, pet.id, householdId]);

  useEffect(() => {
    if (activeTab !== 'notes') return;
    setNotesLoading(true);
    getDocs(query(
      collection(db, 'households', householdId, 'pets', pet.id, 'notes'),
      orderBy('createdAt', 'desc'),
    ))
      .then(snap => setNotes(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))))
      .catch(() => {})
      .finally(() => setNotesLoading(false));
  }, [activeTab, pet.id, householdId]);

  // ── Persist helper ────────────────────────────────────────────────────────────

  const persist = async (fields: Partial<ExtendedPet>) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'households', householdId, 'pets', pet.id), fields as Record<string, any>);
      const updated = { ...petData, ...fields };
      setPetData(updated);
      onUpdate(updated);
    } finally {
      setSaving(false);
    }
  };

  // ── Section saves ─────────────────────────────────────────────────────────────

  const saveProfile = async () => {
    await persist({
      name: profileDraft.name ?? petData.name,
      breed: profileDraft.breed ?? petData.breed,
      birthdateOrAge: profileDraft.birthdateOrAge ?? petData.birthdateOrAge,
      weight: profileDraft.weight ?? petData.weight,
      microchipId: profileDraft.microchipId ?? petData.microchipId,
      color: profileDraft.color ?? petData.color,
    });
    setEditingProfile(false);
  };

  const saveRoutine = async () => {
    await persist({ routine: { ...(petData.routine ?? {}), ...routineDraft } });
    setEditingRoutine(false);
  };

  const saveVet = async () => {
    await persist({ vet: { ...(petData.vet ?? {}), ...vetDraft } });
    setEditingVet(false);
  };

  const saveEVet = async () => {
    await persist({ emergencyVet: { ...(petData.emergencyVet ?? {}), ...eVetDraft } });
    setEditingEVet(false);
  };

  const addMedication = async () => {
    if (!newMed.name || !newMed.dose) return;
    setSavingMed(true);
    const med: Medication = { id: Date.now().toString(), ...newMed };
    await persist({ medications: [...(petData.medications ?? []), med] });
    setNewMed({ name: '', dose: '', frequency: '', startDate: '', notes: '' });
    setShowAddMed(false);
    setSavingMed(false);
  };

  const removeMedication = (id: string) =>
    persist({ medications: (petData.medications ?? []).filter(m => m.id !== id) });

  const saveInsurance = async () => {
    await persist({ insurance: { ...(petData.insurance ?? {}), ...insDraft } });
    setEditingIns(false);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const ref = await addDoc(
        collection(db, 'households', householdId, 'pets', pet.id, 'notes'),
        { text: newNote.trim(), createdAt: new Date().toISOString() },
      );
      setNotes(prev => [{ id: ref.id, text: newNote.trim(), createdAt: new Date().toISOString() }, ...prev]);
      setNewNote('');
    } finally {
      setSavingNote(false);
    }
  };

  const r = petData.routine ?? {};
  const vet = petData.vet ?? {};
  const eVet = petData.emergencyVet ?? {};
  const ins = petData.insurance ?? {};

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-page-settings mx-auto p-4 md:p-6 pb-32 animate-in fade-in duration-300">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span className="text-5xl">{SPECIES_ICONS[petData.species] ?? '🐾'}</span>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-black tracking-tighter text-slate-900 dark:text-white truncate">
            {petData.name}
          </h1>
          <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mt-0.5">
            {[petData.species, petData.breed, petData.birthdateOrAge].filter(Boolean).join(' · ')}
          </p>
        </div>
        {saving && <Loader2 className="w-4 h-4 text-brand animate-spin flex-shrink-0" />}
      </div>

      <TabBar
        tabs={[
          { id: 'profile',   label: t('pets.tab_profile', 'Profile') },
          { id: 'routine',   label: t('pets.tab_routine', 'Routine') },
          { id: 'medical',   label: t('pets.tab_medical', 'Medical') },
          { id: 'notes',     label: t('pets.tab_notes', 'Notes') },
          { id: 'insurance', label: t('pets.tab_insurance', 'Insurance') },
        ]}
        activeTab={activeTab}
        onChange={id => setActiveTab(id as ProfileTab)}
        variant="underline"
        className="mb-6"
      />

      {/* ── PROFILE ──────────────────────────────────────────────────────────── */}
      {activeTab === 'profile' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-400">{t('pets.basic_info', 'Basic Info')}</h2>
            {editingProfile ? (
              <div className="flex gap-2">
                <button onClick={() => setEditingProfile(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
                <button onClick={saveProfile} className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand-hover transition-colors">
                  <Save className="w-3 h-3" /> Save
                </button>
              </div>
            ) : (
              <button onClick={() => { setProfileDraft({}); setEditingProfile(true); }} className="flex items-center gap-1.5 px-3 py-1.5 text-brand text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand/10 transition-colors">
                <Edit2 className="w-3 h-3" /> Edit
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name" value={petData.name} editing={editingProfile} inputKey="name" draft={profileDraft} setDraft={setProfileDraft} />
            <Field label="Breed" value={petData.breed} editing={editingProfile} inputKey="breed" draft={profileDraft} setDraft={setProfileDraft} placeholder="e.g. Labrador Retriever" />
            <Field label="Date of Birth / Age" value={petData.birthdateOrAge} editing={editingProfile} inputKey="birthdateOrAge" draft={profileDraft} setDraft={setProfileDraft} placeholder="e.g. 2021-03-15 or 3 years" />
            <Field label="Weight" value={petData.weight} editing={editingProfile} inputKey="weight" draft={profileDraft} setDraft={setProfileDraft} placeholder="e.g. 25 lbs" />
            <Field label="Color / Markings" value={petData.color} editing={editingProfile} inputKey="color" draft={profileDraft} setDraft={setProfileDraft} placeholder="e.g. Golden with white chest patch" />
            <Field label="Microchip ID" value={petData.microchipId} editing={editingProfile} inputKey="microchipId" draft={profileDraft} setDraft={setProfileDraft} placeholder="15-digit ID number" />
          </div>
        </div>
      )}

      {/* ── ROUTINE ──────────────────────────────────────────────────────────── */}
      {activeTab === 'routine' && (
        <div className="space-y-4">
          {/* Shared save/cancel bar when editing */}
          {editingRoutine && (
            <div className="flex items-center justify-between px-4 py-3 bg-brand/5 border border-brand/20 rounded-2xl">
              <p className="text-2xs font-black uppercase tracking-widest text-brand">{t('pets.editing_routine_hint', 'Editing routine — changes apply to all sections')}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditingRoutine(false)} className="px-3 py-1.5 text-slate-500 text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">Cancel</button>
                <button onClick={saveRoutine} className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand-hover transition-colors">
                  <Save className="w-3 h-3" /> Save All
                </button>
              </div>
            </div>
          )}

          {/* Walks */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Footprints className="w-4 h-4" />
                <h3 className="text-xs font-black uppercase tracking-widest">{t('pets.walks', 'Walks')}</h3>
              </div>
              {!editingRoutine && (
                <button onClick={() => { setRoutineDraft({}); setEditingRoutine(true); }} className="flex items-center gap-1.5 px-3 py-1.5 text-brand text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand/10 transition-colors">
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Frequency" value={r.walkFrequency} editing={editingRoutine} inputKey="walkFrequency" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. 2× daily" />
              <Field label="Duration" value={r.walkDuration} editing={editingRoutine} inputKey="walkDuration" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. 30 minutes" />
              <Field label="Times" value={r.walkTimes} editing={editingRoutine} inputKey="walkTimes" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. 7am & 6pm" />
            </div>
          </div>

          {/* Feeding */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <UtensilsCrossed className="w-4 h-4" />
              <h3 className="text-xs font-black uppercase tracking-widest">{t('pets.feeding', 'Feeding')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Food Brand" value={r.feedingBrand} editing={editingRoutine} inputKey="feedingBrand" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. Royal Canin" />
              <Field label="Portion Size" value={r.feedingPortion} editing={editingRoutine} inputKey="feedingPortion" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. 1 cup" />
              <Field label="Feeding Times" value={r.feedingTimes} editing={editingRoutine} inputKey="feedingTimes" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. 7am & 6pm" />
            </div>
          </div>

          {/* Grooming */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Scissors className="w-4 h-4" />
              <h3 className="text-xs font-black uppercase tracking-widest">{t('pets.grooming', 'Grooming')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Frequency" value={r.groomingFrequency} editing={editingRoutine} inputKey="groomingFrequency" draft={routineDraft} setDraft={setRoutineDraft} placeholder="e.g. Every 6 weeks" />
              <Field label="Last Groomed" value={r.lastGroomed} editing={editingRoutine} inputKey="lastGroomed" draft={routineDraft} setDraft={setRoutineDraft} type="date" />
            </div>
          </div>

          {/* Training */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <BookOpen className="w-4 h-4" />
              <h3 className="text-xs font-black uppercase tracking-widest">{t('pets.training_notes', 'Training Notes')}</h3>
            </div>
            {editingRoutine ? (
              <textarea
                value={routineDraft.trainingNotes ?? r.trainingNotes ?? ''}
                onChange={e => setRoutineDraft(d => ({ ...d, trainingNotes: e.target.value }))}
                rows={4}
                placeholder="Commands learned, ongoing training goals, behavioral notes..."
                className="w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 resize-none"
              />
            ) : (
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                {r.trainingNotes || <span className="text-slate-400 italic">No training notes yet.</span>}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── MEDICAL ──────────────────────────────────────────────────────────── */}
      {activeTab === 'medical' && (
        <div className="space-y-4">

          {/* Primary Vet */}
          <SectionCard
            title="Primary Veterinarian"
            icon={<Stethoscope className="w-4 h-4" />}
            editing={editingVet}
            onEdit={() => { setVetDraft({}); setEditingVet(true); }}
            onCancel={() => setEditingVet(false)}
            onSave={saveVet}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Clinic / Vet Name" value={vet.name} editing={editingVet} inputKey="name" draft={vetDraft} setDraft={setVetDraft} placeholder="e.g. Happy Paws Clinic" />
              <Field label="Phone" value={vet.phone} editing={editingVet} inputKey="phone" draft={vetDraft} setDraft={setVetDraft} placeholder="+1 (555) 000-0000" />
              <Field label="Address" value={vet.address} editing={editingVet} inputKey="address" draft={vetDraft} setDraft={setVetDraft} placeholder="123 Main St, City, State" />
              <Field label="Last Visit" value={vet.lastVisit} editing={editingVet} inputKey="lastVisit" draft={vetDraft} setDraft={setVetDraft} type="date" />
            </div>
            {!editingVet && vet.phone && (
              <a href={`tel:${vet.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-brand text-xs font-bold hover:underline">
                <Phone className="w-3.5 h-3.5" /> Call Vet
              </a>
            )}
          </SectionCard>

          {/* Emergency Vet */}
          <SectionCard
            title="Nearest Emergency Vet"
            icon={<AlertTriangle className="w-4 h-4" />}
            editing={editingEVet}
            onEdit={() => { setEVetDraft({}); setEditingEVet(true); }}
            onCancel={() => setEditingEVet(false)}
            onSave={saveEVet}
            accent="amber"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Clinic Name" value={eVet.name} editing={editingEVet} inputKey="name" draft={eVetDraft} setDraft={setEVetDraft} placeholder="e.g. Animal Emergency Center" />
              <Field label="Phone" value={eVet.phone} editing={editingEVet} inputKey="phone" draft={eVetDraft} setDraft={setEVetDraft} placeholder="+1 (555) 000-0000" />
              <Field label="Address" value={eVet.address} editing={editingEVet} inputKey="address" draft={eVetDraft} setDraft={setEVetDraft} placeholder="123 Emergency Ave, City" />
              <Field label="Estimated Distance" value={eVet.distance} editing={editingEVet} inputKey="distance" draft={eVetDraft} setDraft={setEVetDraft} placeholder="e.g. 2.4 miles" />
            </div>
            {!editingEVet && eVet.phone && (
              <a href={`tel:${eVet.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-amber-500 text-xs font-bold hover:underline">
                <Phone className="w-3.5 h-3.5" /> Emergency Line
              </a>
            )}
          </SectionCard>

          {/* Current Medications */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Pill className="w-4 h-4" />
                <h3 className="text-xs font-black uppercase tracking-widest">{t('pets.current_medications', 'Current Medications')}</h3>
              </div>
              <button
                onClick={() => setShowAddMed(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-lg hover:bg-brand-hover transition-colors"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>

            {petData.medications?.length ? (
              <div className="space-y-2">
                {petData.medications.map(med => (
                  <div key={med.id} className="flex items-start justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm text-slate-900 dark:text-white">{med.name}</p>
                      <p className="text-2xs text-slate-500 mt-0.5 font-medium">
                        {[med.dose, med.frequency].filter(Boolean).join(' · ')}
                      </p>
                      {med.startDate && <p className="text-2xs text-slate-400">Started {med.startDate}</p>}
                      {med.notes && <p className="text-2xs text-slate-400 italic mt-0.5">{med.notes}</p>}
                    </div>
                    <button onClick={() => removeMedication(med.id)} className="p-1.5 ml-2 text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : !showAddMed ? (
              <p className="text-sm text-slate-400 italic">{t('pets.no_medications', 'No current medications on file.')}</p>
            ) : null}

            {showAddMed && (
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-3 border border-slate-200 dark:border-slate-700">
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400">New Medication</p>
                <input value={newMed.name} onChange={e => setNewMed(p => ({ ...p, name: e.target.value }))} placeholder="Medication name *" className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 text-slate-900 dark:text-white" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={newMed.dose} onChange={e => setNewMed(p => ({ ...p, dose: e.target.value }))} placeholder="Dose (e.g. 10mg) *" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 text-slate-900 dark:text-white" />
                  <input value={newMed.frequency} onChange={e => setNewMed(p => ({ ...p, frequency: e.target.value }))} placeholder="Frequency (e.g. Daily)" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 text-slate-900 dark:text-white" />
                  <input type="date" value={newMed.startDate} onChange={e => setNewMed(p => ({ ...p, startDate: e.target.value }))} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 text-slate-900 dark:text-white" />
                  <input value={newMed.notes} onChange={e => setNewMed(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 text-slate-900 dark:text-white" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setShowAddMed(false)} className="flex-1 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-500 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">Cancel</button>
                  <button onClick={addMedication} disabled={!newMed.name || !newMed.dose || savingMed} className="flex-1 py-2.5 rounded-xl bg-brand text-white text-xs font-bold hover:bg-brand-hover disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                    {savingMed ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Add Medication
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Medical History from CF */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Calendar className="w-4 h-4" />
              <h3 className="text-xs font-black uppercase tracking-widest">{t('pets.visit_history', 'Visit History')}</h3>
            </div>
            {medicalLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-brand animate-spin" /></div>
            ) : medicalRecords.length === 0 ? (
              <p className="text-sm text-slate-400 italic">{t('pets.no_visit_history', 'No visit history on file.')}</p>
            ) : (
              <div className="space-y-2">
                {medicalRecords.map(rec => (
                  <div key={rec.id} className="flex items-start justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl gap-4">
                    <div className="min-w-0">
                      <p className="text-2xs font-black uppercase tracking-widest text-brand">{rec.type.replace(/_/g, ' ')}</p>
                      <p className="font-bold text-sm text-slate-900 dark:text-white mt-0.5">{rec.date}</p>
                      {rec.notes && <p className="text-xs text-slate-500 mt-1">{rec.notes}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {rec.nextDueDate && (
                        <div className="flex items-center gap-1 text-xs text-amber-500 font-medium">
                          <Calendar className="w-3 h-3" /> {rec.nextDueDate}
                        </div>
                      )}
                      {rec.cost !== undefined && (
                        <p className="text-2xs text-slate-400 mt-1">${rec.cost.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── NOTES ────────────────────────────────────────────────────────────── */}
      {activeTab === 'notes' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">{t('pets.add_note', 'Add Note')}</h3>
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              rows={3}
              placeholder="Log a behavior, vet call, observation, milestone, or anything worth remembering..."
              className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-brand/40 resize-none border border-slate-200 dark:border-slate-700"
            />
            <button
              onClick={addNote}
              disabled={!newNote.trim() || savingNote}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-xl hover:bg-brand-hover disabled:opacity-50 transition-colors"
            >
              {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {t('pets.save_note', 'Save Note')}
            </button>
          </div>

          {notesLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-brand animate-spin" /></div>
          ) : notes.length === 0 ? (
            <EmptyState
              icon={<FileText className="w-8 h-8 text-slate-400" />}
              title={t('pets.no_notes_title', 'No notes yet')}
              description={t('pets.no_notes_desc', 'Track observations, behavior changes, vet call notes, and more.')}
            />
          ) : (
            <div className="space-y-3">
              {notes.map(note => (
                <div key={note.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-2">
                    {new Date(note.createdAt).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{note.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── INSURANCE ────────────────────────────────────────────────────────── */}
      {activeTab === 'insurance' && (
        <div className="space-y-4">
          <SectionCard
            title="Insurance Policy"
            icon={<Shield className="w-4 h-4" />}
            editing={editingIns}
            onEdit={() => { setInsDraft({}); setEditingIns(true); }}
            onCancel={() => setEditingIns(false)}
            onSave={saveInsurance}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Insurance Provider" value={ins.provider} editing={editingIns} inputKey="provider" draft={insDraft} setDraft={setInsDraft} placeholder="e.g. Nationwide Pet" />
              <Field label="Policy Number" value={ins.policyNumber} editing={editingIns} inputKey="policyNumber" draft={insDraft} setDraft={setInsDraft} placeholder="e.g. PET-12345678" />
              <Field label="Provider Phone" value={ins.phone} editing={editingIns} inputKey="phone" draft={insDraft} setDraft={setInsDraft} placeholder="+1 (800) 000-0000" />
              <Field label="Coverage Type" value={ins.coverageType} editing={editingIns} inputKey="coverageType" draft={insDraft} setDraft={setInsDraft} placeholder="e.g. Accident & Illness" />
              <Field label="Monthly Premium" value={ins.monthlyPremium} editing={editingIns} inputKey="monthlyPremium" draft={insDraft} setDraft={setInsDraft} placeholder="e.g. $45.00/mo" />
              <Field label="Renewal Date" value={ins.renewalDate} editing={editingIns} inputKey="renewalDate" draft={insDraft} setDraft={setInsDraft} type="date" />
            </div>
            {editingIns ? (
              <div className="mt-1">
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Notes</p>
                <textarea
                  value={insDraft.notes ?? ins.notes ?? ''}
                  onChange={e => setInsDraft(d => ({ ...d, notes: e.target.value }))}
                  rows={2}
                  placeholder="Deductible, reimbursement rate, exclusions..."
                  className="w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-brand/40 resize-none"
                />
              </div>
            ) : ins.notes ? (
              <div className="mt-1">
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">Notes</p>
                <p className="text-sm text-slate-700 dark:text-slate-300">{ins.notes}</p>
              </div>
            ) : null}
            {!editingIns && ins.phone && (
              <a href={`tel:${ins.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-brand text-xs font-bold hover:underline">
                <Phone className="w-3.5 h-3.5" /> Call Provider
              </a>
            )}
          </SectionCard>

          {!ins.provider && !editingIns && (
            <EmptyState
              icon={<Shield className="w-8 h-8 text-slate-400" />}
              title={t('pets.no_insurance_title', 'No insurance on file')}
              description={t('pets.no_insurance_desc', 'Add your pet insurance details to keep policy info and emergency contacts in one place.')}
            />
          )}
        </div>
      )}
    </div>
  );
};
